import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { HeightSize, InputSize } from '~/constants/form';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import * as React from 'react';
import { colors, fontFamily, formSizes } from '~/styles/constants';

interface ButtonProps {
  hasOptionSelected: boolean;
  size?: number;
  disabled: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  type: string;
  responsive?: boolean;
  className?: string;
  tabIndex?: number;
  isMultiSelect?: boolean;
  height?: HeightSize;
  ref?: (node: React.ReactNode) => void;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SelectButtonStyled = styled('button')<ButtonProps & any>`
  background: none;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 3px;
  font-family: ${fontFamily};
  line-height: ${(props) => (props.height ? `${props.height}px` : formSizes.inputHeight)};
  ${(props) =>
    props.isMultiSelect && props.hasOptionSelected
      ? 'height: unset;'
      : `height: ${props.height ? `${props.height}px` : formSizes.inputHeight}`};

  width: 100%;

  /* Tiny screens (iPhone 5) */
  @media screen and (min-width: ${ResponsiveBreakPoints.ExtraSmallMobile}px) {
    min-width: 66px;
  }

  /* Tablet and above */
  @media screen and (min-width: ${ResponsiveBreakPoints.Tablet}px) {
    ${(props) => (props.responsive ? 'width: 100%;' : `width: ${props.size}px;`)};
  }
  text-align: left;
  padding: ${(props) => (props.isMultiSelect ? '3px 3px 0 3px;' : '0 18px 0 6px;')};
  font-size: 14px;
  color: ${(props) => (props.hasOptionSelected ? colors.form.fontColor : colors.placeholderColor)};
  position: relative;
  background-color: white;
  outline: none;

  :focus,
  &:active {
    border-color: ${colors.form.activeBorderColor};
  }

  :disabled {
    background-color: ${colors.form.greyDisabled};
    color: ${colors.form.disabledLabelFontColor};
    border-color: ${colors.form.greyDisabledBorder};
  }
`;

const ButtonIconContainer = styled('span')<{ disabled?: boolean }>`
  position: absolute;
  right: 12px;
  font-size: 12px;
  color: ${(props) => (props.disabled ? colors.form.greyDisabledButton : colors.placeholderColor)};
  top: 50%;
  transform: translateY(-50%);
`;

const TextContainer = styled('div')<{ isMultiSelect?: boolean }>`
  white-space: ${(props) => (props.isMultiSelect ? 'initial' : 'nowrap')};
  line-height: initial;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 30px);
  min-width: 30px;
`;

export interface SelectButtonProps {
  disabled?: boolean;
  text?: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  hasOptionSelected?: boolean;
  size?: InputSize;
  responsive?: boolean;
  className?: string;
  tabIndex?: number;
  invalid?: boolean;
  isMultiSelect?: boolean;
  height?: HeightSize;
  dataAnalytics?: string;
  dataPendo?: string;
  dataE2E?: string;
  dataTestid?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buttonRef?: (node: any) => void;
  children?: React.ReactNode;
}

const SelectButton = (props: SelectButtonProps) => {
  const {
    children,
    disabled,
    onClick,
    hasOptionSelected,
    size,
    responsive,
    className,
    tabIndex,
    invalid,
    isMultiSelect,
    height,
    dataAnalytics,
    dataPendo,
    dataE2E,
    dataTestid,
    buttonRef,
  } = props;
  const buttonSize = size ? size : InputSize.medium;

  const theme = {
    borderColor: invalid ? colors.dangerRed : colors.form.greyBorder,
  };

  return (
    <ThemeProvider theme={theme}>
      <SelectButtonStyled
        disabled={!!disabled}
        onClick={onClick}
        hasOptionSelected={!!hasOptionSelected}
        size={buttonSize}
        type="button"
        responsive={responsive}
        className={className}
        tabIndex={tabIndex}
        isMultiSelect={isMultiSelect}
        height={height}
        data-analytics={dataAnalytics}
        data-pendo={dataPendo}
        data-e2e={dataE2E}
        data-testid={dataTestid}
        ref={buttonRef}
      >
        <TextContainer isMultiSelect={isMultiSelect}>{children}</TextContainer>
        <ButtonIconContainer disabled={disabled}>
          <i className="fa fa-chevron-down" />
        </ButtonIconContainer>
      </SelectButtonStyled>
    </ThemeProvider>
  );
};

export default SelectButton;
